import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listTriageSessions, type TriageSessionStatus } from '../api/triage';
import { formatDate } from '../lib/format';

const STATUS_OPTIONS: Array<{ value: TriageSessionStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'paused_for_input', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'aborted', label: 'Aborted' },
];

const STATUS_BADGE: Record<TriageSessionStatus, string> = {
  in_progress: 'bg-blue-100 text-blue-800',
  paused_for_input: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  aborted: 'bg-gray-100 text-gray-700',
};

export function TriageSessionsList(): React.ReactElement {
  const [status, setStatus] = useState<TriageSessionStatus | ''>('');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['triage-sessions', { status }],
    queryFn: () => listTriageSessions({ status: status || undefined, limit: 50 }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Triage Sessions</h1>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as TriageSessionStatus | '')}
          className="text-sm border border-border rounded-md px-3 py-1.5"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <ul role="list" className="space-y-3">
        {items.map((s) => (
          <li
            key={s.sessionId}
            onClick={() => navigate(`/triage-sessions/${s.sessionId}`)}
            className="bg-white border border-border rounded-lg p-4 cursor-pointer hover:border-brand-500 hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[s.status]}`}>
                  {s.status}
                </span>
                <span className="text-sm text-text-secondary">{s.actorEmail}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-text-muted">
                <span>{s.findingsExamined.length} findings</span>
                <span>{s.prsOpened.length} PRs</span>
                <span>{formatDate(s.startedAt)}</span>
              </div>
            </div>
            {s.allowAutoMerge && (
              <p className="text-xs text-amber-700 mt-1">Auto-merge enabled</p>
            )}
          </li>
        ))}
        {!isLoading && items.length === 0 && (
          <li className="text-sm text-center py-8 text-text-muted">No triage sessions.</li>
        )}
      </ul>
    </div>
  );
}
